import { omit } from 'lodash';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDrumHitScheduler } from '../../../hooks';
import { useAutoStop, useDemo, usePracticeClock, useRecorder, useSheetPlayerBot } from '../hooks';
import { EvaluationContextProvider } from './EvaluationContext';
import { useExerciseContext } from './ExerciseContext';
export function usePracticeContext() {
    const context = useContext(PracticeContext);
    if (context == null) {
        throw new Error('No practice context');
    }
    return context;
}
export const PracticeContext = React.createContext(null);
export function PracticeContextProvider(props) {
    if (useContext(PracticeContext) != null) {
        throw new Error('PracticeContextProvider can only be used once');
    }
    const [practiceMode, setPracticeMode] = useState('view');
    const { exercise, tempo, metronome, evaluator, metronomeSheet, sequencerConfig } = useExerciseContext();
    const { pads } = exercise;
    const { stepIndex, children, saveRecording, } = props;
    const { practice, timing } = evaluator;
    const { clock, clockState, sequencer } = usePracticeClock(timing, sequencerConfig);
    const [latestEvaluation, setLatestEvaluation] = useState({ stepIndex, evaluator: null });
    useEffect(() => {
        setPracticeMode('view');
    }, [exercise]);
    useEffect(() => {
        clock.reset();
    }, [clock, practiceMode]);
    const hitScheduler = useDrumHitScheduler(clock, 'standard_set');
    useSheetPlayerBot(metronomeSheet, timing, clock, clockState, hitScheduler);
    useDemo(clock, clockState, evaluator, practiceMode, hitScheduler);
    const messagesRef = useRef([]);
    const { getRecording } = useRecorder(clock, clockState, evaluator, messagesRef, practiceMode);
    const startDemo = useCallback(() => {
        setPracticeMode('demo');
        clock.restart();
    }, [clock, setPracticeMode]);
    const stopDemo = useCallback(() => {
        setPracticeMode('view');
        clock.reset();
    }, [clock, setPracticeMode]);
    const startRecording = useCallback(() => {
        setPracticeMode('recording');
        evaluator.reset();
        clock.restart();
    }, [clock, evaluator]);
    const updateLatestEvaluation = useCallback(() => {
        setLatestEvaluation({ stepIndex, evaluator });
    }, [evaluator, stepIndex]);
    const handleRecordingEnd = useCallback(async (endState) => {
        const options = {
            tempo, pads,
            signature: omit(evaluator.timing.signature, 'tickNote'),
            tickNote: evaluator.timing.signature.tickNote ?? evaluator.timing.denominator,
            metronome, leadIn: 0, leadOut: 0
        };
        const recording = { ...getRecording(), endState, options, messages: [...messagesRef.current] };
        messagesRef.current = [];
        await saveRecording(recording);
    }, [
        evaluator.timing.denominator, evaluator.timing.signature, getRecording, metronome, pads,
        saveRecording, tempo
    ]);
    const restartRecording = useCallback(async () => {
        handleRecordingEnd('restarted');
        await clock.reset();
        evaluator.reset();
        await clock.resume();
    }, [clock, evaluator, handleRecordingEnd]);
    const stopRecording = useCallback(async () => {
        handleRecordingEnd('stopped');
        await clock.reset();
        evaluator.reset();
        setPracticeMode('view');
    }, [clock, evaluator, handleRecordingEnd]);
    const finishRecording = useCallback(async () => {
        await handleRecordingEnd('finished');
        clock.reset();
        updateLatestEvaluation();
        setPracticeMode('evaluate');
    }, [clock, handleRecordingEnd, updateLatestEvaluation]);
    const startEvaluating = useCallback(() => {
        updateLatestEvaluation();
        setPracticeMode('evaluate');
    }, [updateLatestEvaluation]);
    const resetView = useCallback(() => {
        setPracticeMode('view');
        clock.reset();
    }, [clock]);
    const handleStop = useCallback(() => {
        switch (practiceMode) {
            case 'recording':
                return finishRecording();
            case 'demo':
                return stopDemo();
            case 'evaluate':
                return clock.reset();
            case 'replay':
                return clock.reset();
            case 'view':
                return clock.reset();
        }
    }, [clock, finishRecording, practiceMode, stopDemo]);
    useAutoStop(clock, clockState, timing, handleStop);
    const actions = {
        startRecording, restartRecording,
        stopRecording, startDemo, stopDemo,
        startEvaluating, resetView
    };
    const context = {
        practice, evaluator, pads,
        sequencer, metronomeSheet,
        clock, clockState, actions, practiceMode, messagesRef,
        latestEvaluation
    };
    return React.createElement(PracticeContext.Provider, { value: context },
        React.createElement(EvaluationContextProvider, { ...latestEvaluation }, children));
}
//# sourceMappingURL=PracticeContext.js.map